import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Check, MapPin, MessageCircle, Truck, Trash2, Layers, Boxes, Sparkles, Recycle, Hammer, Package } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";
import { VISIBLE_SERVICES as SERVICES, CANTONS_SEO, SITE_URL, buildBreadcrumbLd } from "@/lib/seo";
import { useI18n } from "@/lib/i18n";
import { SERVICE_NAME_I18N } from "@/lib/seoPagesI18n";

const SERVICE_ICONS = {
  demenagement: Truck, debarras: Trash2, desencombrement: Layers, "home-organizing": Boxes,
  nettoyage: Sparkles, recyclage: Recycle, "montage-de-meubles": Hammer, "transport-de-meubles": Package,
};

// Descriptions courtes + ancres par slug (FR/DE/EN) — les 5 services visibles.
const CARD_I18N = {
  fr: {
    desc: {
      demenagement: "Déménagement complet ou partiel, avec protection, transport et installation.",
      debarras: "Nous vidons vos espaces encombrés rapidement, proprement et de façon responsable.",
      nettoyage: "Nettoyage de fin de bail, après déménagement ou remise en état d'un logement.",
      "transport-de-meubles": "Transport de meubles, canapés, lits ou objets volumineux en Suisse et à l'international.",
      "home-organizing": "Organisation professionnelle de vos pièces, placards, dressing et cuisine.",
    },
    anchor: {
      demenagement: "Découvrir notre service de déménagement", debarras: "En savoir plus sur le débarras",
      nettoyage: "Voir notre nettoyage fin de bail", "transport-de-meubles": "Voir notre service de transport de meubles",
      "home-organizing": "Découvrir le home organizing",
    },
  },
  de: {
    desc: {
      demenagement: "Vollständiger oder teilweiser Umzug mit Schutz, Transport und Aufbau.",
      debarras: "Wir leeren überfüllte Räume schnell, sauber und verantwortungsvoll.",
      nettoyage: "Endreinigung, Reinigung nach Umzug oder Wiederherstellung einer Wohnung.",
      "transport-de-meubles": "Transport von Möbeln, Sofas, Betten oder sperrigen Objekten – national und international.",
      "home-organizing": "Professionelle Organisation Ihrer Räume, Schränke, Ankleiden und Küchen.",
    },
    anchor: {
      demenagement: "Umzug entdecken", debarras: "Mehr zur Entrümpelung",
      nettoyage: "Unsere Endreinigung ansehen", "transport-de-meubles": "Unseren Möbeltransport ansehen",
      "home-organizing": "Home Organizing entdecken",
    },
  },
  en: {
    desc: {
      demenagement: "Full or partial moves with protection, transport and setup.",
      debarras: "We empty cluttered spaces quickly, cleanly and responsibly.",
      nettoyage: "End-of-lease cleaning, post-move cleaning or restoring a home.",
      "transport-de-meubles": "Transport of furniture, sofas, beds or bulky items — national and international.",
      "home-organizing": "Professional organization of your rooms, closets, dressing and kitchen.",
    },
    anchor: {
      demenagement: "Discover our moving service", debarras: "Learn more about clear-outs",
      nettoyage: "See our end-of-lease cleaning", "transport-de-meubles": "See our furniture transport service",
      "home-organizing": "Discover home organizing",
    },
  },
};

const L = {
  fr: {
    seoTitle: "Nos services : déménagement, débarras & nettoyage | Mopper",
    seoDesc: "Découvrez les services Mopper en Suisse romande : déménagement, débarras, nettoyage fin de bail et transport de meubles.",
    bcHome: "Accueil", bcServices: "Services",
    h1: "Tous les services Mopper",
    heroP: "Mopper est l'entreprise suisse multiservices qui s'occupe de tout ce qui vous encombre. Déménagement, débarras, nettoyage fin de bail et transport de meubles : nous combinons plusieurs prestations en un seul interlocuteur, partout en Suisse romande.",
    ctaQuote: "Demander mon devis gratuit", ctaWhatsapp: "Nous contacter sur WhatsApp",
    s1Kicker: "Quand faire appel à Mopper\u00a0?", s1H2: "Une seule équipe, plusieurs prestations.",
    s1P: "Nos quatre services peuvent être combinés librement. Vous n'avez qu'un seul interlocuteur, un seul devis, une seule équipe sur place. Le périmètre est défini ensemble : volume, accès, distance, contraintes du logement, présence ou absence d'ascenseur. Le prix final dépend de la réalité de votre demande, pas d'un forfait abstrait.",
    useCases: [
      "Vous changez de logement et avez besoin d'un seul prestataire pour le déménagement et le nettoyage de l'ancien appartement.",
      "Une cave, un grenier ou un garage encombré que vous voulez vider en une journée.",
      "Une succession ou un débarras après décès à gérer avec discrétion et respect.",
      "Un appartement à remettre en état pour un état des lieux, avec garantie de restitution.",
      "Un meuble lourd ou volumineux à transporter en Suisse romande, sans louer un camion.",
      "Le besoin de repartir sur des bases plus claires : tri et remise en ordre de vos espaces.",
    ],
    s2Kicker: "Nos services", s2H2: "Choisissez la prestation adaptée à votre besoin.", s2Cta: "Demander un devis",
    combKicker: "Services combinables", combH2: "Un seul devis, plusieurs prestations.",
    combP: "Déménager et nettoyer l'ancien logement, vider une cave avant un déménagement, transporter un meuble entre deux adresses : Mopper synchronise tout dans une seule organisation. Vous n'avez pas à coordonner plusieurs entreprises.",
    combBullets: ["Devis combiné, sans double facturation", "Une même équipe, un même interlocuteur", "Planning ajusté à vos contraintes"],
    stepsKicker: "Comment se déroule une demande\u00a0?", stepsH2: "Quatre étapes, sans engagement.",
    steps: [
      "Vous décrivez votre besoin via le formulaire ou WhatsApp (quelques photos suffisent).",
      "Nous étudions le périmètre : volume, distance, accès, étage, ascenseur.",
      "Nous vous envoyons un devis clair sous 24 heures, services détaillés.",
      "Une équipe Mopper intervient à la date convenue.",
    ],
    stepsCta: "Démarrer ma demande",
    zonesKicker: "Zones d'intervention", zonesH2: "Mopper intervient partout en Suisse romande.",
    zonesP1: "Notre zone principale est le ", zonesVaud: "canton de Vaud",
    zonesP2: ", avec Lausanne, Morges, Renens, Vevey, Montreux et Yverdon-les-Bains. Nous intervenons également à Genève, Fribourg, Neuchâtel, en Valais et dans le Jura selon la prestation et la distance.",
    zonesCta: "Voir toutes les zones d'intervention",
    finalH2a: "Un projet à ", finalH2b: "simplifier", finalP: "Devis gratuit sous 24h · Sans engagement · Intervention rapide",
    finalCta: "Demander un devis gratuit",
  },
  de: {
    seoTitle: "Unsere Leistungen: Umzug, Entrümpelung & Reinigung | Mopper",
    seoDesc: "Entdecken Sie die Mopper-Leistungen in der Westschweiz: Umzug, Entrümpelung, Endreinigung und Möbeltransport.",
    bcHome: "Startseite", bcServices: "Leistungen",
    h1: "Alle Mopper-Leistungen",
    heroP: "Mopper ist das Schweizer Multi-Service-Unternehmen, das sich um alles kümmert, was Sie überfordert. Umzug, Entrümpelung, Endreinigung und Möbeltransport: Wir bündeln mehrere Leistungen bei einem einzigen Ansprechpartner – überall in der Westschweiz.",
    ctaQuote: "Gratis-Angebot anfordern", ctaWhatsapp: "Kontakt über WhatsApp",
    s1Kicker: "Wann Mopper beauftragen\u00a0?", s1H2: "Ein Team, mehrere Leistungen.",
    s1P: "Unsere vier Leistungen lassen sich frei kombinieren. Sie haben nur einen Ansprechpartner, ein Angebot, ein Team vor Ort. Der Umfang wird gemeinsam festgelegt: Volumen, Zugang, Distanz, Wohnungsgegebenheiten, Lift oder nicht. Der Endpreis richtet sich nach der Realität Ihrer Anfrage, nicht nach einer abstrakten Pauschale.",
    useCases: [
      "Sie wechseln die Wohnung und brauchen einen einzigen Anbieter für Umzug und Reinigung der alten Wohnung.",
      "Ein überfüllter Keller, Estrich oder eine Garage, die Sie an einem Tag leeren möchten.",
      "Ein Nachlass oder eine Räumung nach einem Todesfall, mit Diskretion und Respekt.",
      "Eine Wohnung, die für die Abnahme wiederhergestellt werden muss, mit Rückgabegarantie.",
      "Ein schweres oder sperriges Möbelstück, das in der Westschweiz transportiert werden soll – ohne Lkw-Miete.",
      "Der Wunsch, klarer neu zu starten: Sortieren und Ordnung in Ihren Räumen.",
    ],
    s2Kicker: "Unsere Leistungen", s2H2: "Wählen Sie die passende Leistung für Ihren Bedarf.", s2Cta: "Angebot anfordern",
    combKicker: "Kombinierbare Leistungen", combH2: "Ein Angebot, mehrere Leistungen.",
    combP: "Umziehen und die alte Wohnung reinigen, einen Keller vor dem Umzug leeren, ein Möbelstück zwischen zwei Adressen transportieren: Mopper synchronisiert alles in einer einzigen Organisation. Sie müssen keine mehreren Firmen koordinieren.",
    combBullets: ["Kombiniertes Angebot, keine Doppelverrechnung", "Ein Team, ein Ansprechpartner", "Planung an Ihre Vorgaben angepasst"],
    stepsKicker: "Wie läuft eine Anfrage ab\u00a0?", stepsH2: "Vier Schritte, unverbindlich.",
    steps: [
      "Sie beschreiben Ihren Bedarf über das Formular oder WhatsApp (ein paar Fotos genügen).",
      "Wir prüfen den Umfang: Volumen, Distanz, Zugang, Etage, Lift.",
      "Wir senden Ihnen innert 24 Stunden ein klares Angebot mit detaillierten Leistungen.",
      "Ein Mopper-Team rückt am vereinbarten Datum aus.",
    ],
    stepsCta: "Meine Anfrage starten",
    zonesKicker: "Einsatzgebiete", zonesH2: "Mopper ist überall in der Westschweiz im Einsatz.",
    zonesP1: "Unser Haupteinsatzgebiet ist der ", zonesVaud: "Kanton Waadt",
    zonesP2: ", mit Lausanne, Morges, Renens, Vevey, Montreux und Yverdon-les-Bains. Wir sind ausserdem in Genf, Freiburg, Neuenburg, im Wallis und im Jura im Einsatz – je nach Leistung und Distanz.",
    zonesCta: "Alle Einsatzgebiete ansehen",
    finalH2a: "Ein Projekt zum ", finalH2b: "Vereinfachen", finalP: "Gratis-Angebot in 24h · Unverbindlich · Schneller Einsatz",
    finalCta: "Gratis-Angebot anfordern",
  },
  en: {
    seoTitle: "Our services: moving, clear-outs & cleaning | Mopper",
    seoDesc: "Discover Mopper's services in French-speaking Switzerland: moving, clear-outs, end-of-lease cleaning and furniture transport.",
    bcHome: "Home", bcServices: "Services",
    h1: "All Mopper services",
    heroP: "Mopper is the Swiss multi-service company that handles everything that clutters you. Moving, clear-outs, end-of-lease cleaning and furniture transport: we combine several services under one single contact, all across French-speaking Switzerland.",
    ctaQuote: "Get my free quote", ctaWhatsapp: "Contact us on WhatsApp",
    s1Kicker: "When to call Mopper\u00a0?", s1H2: "One team, several services.",
    s1P: "Our four services can be freely combined. You get one single contact, one quote, one team on site. The scope is defined together: volume, access, distance, home constraints, lift or not. The final price depends on the reality of your request, not on an abstract flat rate.",
    useCases: [
      "You're changing homes and need a single provider for the move and the cleaning of the old flat.",
      "A cluttered cellar, attic or garage you want emptied in a day.",
      "An estate or a post-bereavement clear-out to handle with discretion and respect.",
      "A flat to restore for a handover, with a return guarantee.",
      "A heavy or bulky item to transport across French-speaking Switzerland, without renting a truck.",
      "The need to start fresh: sorting and tidying your spaces.",
    ],
    s2Kicker: "Our services", s2H2: "Choose the service that fits your need.", s2Cta: "Request a quote",
    combKicker: "Combinable services", combH2: "One quote, several services.",
    combP: "Moving and cleaning the old home, emptying a cellar before a move, transporting furniture between two addresses: Mopper synchronizes everything in a single operation. You don't have to coordinate several companies.",
    combBullets: ["Combined quote, no double billing", "One team, one contact", "Schedule adjusted to your constraints"],
    stepsKicker: "How does a request work\u00a0?", stepsH2: "Four steps, no commitment.",
    steps: [
      "You describe your need via the form or WhatsApp (a few photos are enough).",
      "We study the scope: volume, distance, access, floor, lift.",
      "We send you a clear quote within 24 hours, with detailed services.",
      "A Mopper team intervenes on the agreed date.",
    ],
    stepsCta: "Start my request",
    zonesKicker: "Service areas", zonesH2: "Mopper operates all across French-speaking Switzerland.",
    zonesP1: "Our main area is the ", zonesVaud: "canton of Vaud",
    zonesP2: ", with Lausanne, Morges, Renens, Vevey, Montreux and Yverdon-les-Bains. We also operate in Geneva, Fribourg, Neuchâtel, Valais and Jura depending on the service and the distance.",
    zonesCta: "See all service areas",
    finalH2a: "A project to ", finalH2b: "simplify", finalP: "Free quote within 24h · No commitment · Fast intervention",
    finalCta: "Get a free quote",
  },
};

export default function ServicesIndex() {
  const navigate = useNavigate();
  const { lang } = useI18n();
  const c = L[lang] || L.fr;
  const cardT = CARD_I18N[lang] || CARD_I18N.fr;
  const names = SERVICE_NAME_I18N[lang] || SERVICE_NAME_I18N.fr;
  const breadcrumb = [
    { name: c.bcHome, url: `${SITE_URL}/` },
    { name: c.bcServices, url: `${SITE_URL}/services` },
  ];

  const COLLECTION_LD = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": "Tous les services Mopper",
    "url": `${SITE_URL}/services`,
    "description": "Page centrale présentant les services Mopper : déménagement, débarras, nettoyage fin de bail et transport de meubles en Suisse romande.",
    "isPartOf": { "@id": `${SITE_URL}/#organization` },
    "hasPart": SERVICES.map((s) => ({
      "@type": "Service",
      "name": s.name,
      "url": `${SITE_URL}/services/${s.slug}`,
      "provider": { "@id": `${SITE_URL}/#localbusiness` },
    })),
  };

  return (
    <div className="min-h-screen bg-[#F7F7F8] text-[#0A0F2C]" data-testid="services-index-page">
      <Seo title={c.seoTitle} description={c.seoDesc} path="/services" jsonLd={[COLLECTION_LD, buildBreadcrumbLd(breadcrumb)]} />
      <Header />

      <section className="relative bg-[#2D5BFF] hero-noise pt-32 md:pt-40 pb-16 md:pb-24 text-white overflow-hidden">
        <div className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(ellipse at top right, rgba(255,255,255,0.15), transparent 55%)" }} />
        <div className="relative mx-auto px-6 sm:px-8 md:px-10 lg:px-14" style={{ maxWidth: "1760px" }}>
          <nav aria-label="breadcrumb" className="mb-6 text-xs md:text-sm text-white/70">
            <Link to="/" className="hover:text-white">{c.bcHome}</Link>
            <span className="mx-2">/</span>
            <span className="text-[#FFD84D] font-semibold">{c.bcServices}</span>
          </nav>
          <h1 className="font-extrabold text-4xl md:text-6xl lg:text-7xl tracking-tighter leading-[0.95] max-w-4xl" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }} data-testid="services-index-h1">
            {c.h1}
          </h1>
          <p className="mt-6 text-white/90 text-base md:text-lg lg:text-xl max-w-3xl leading-relaxed">
            {c.heroP}
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <button onClick={() => navigate("/devis")} className="inline-flex items-center justify-center gap-2 rounded-full bg-[#FFD84D] hover:bg-yellow-300 text-[#0A0F2C] px-7 py-4 font-extrabold text-sm md:text-base shadow-[0_15px_30px_-10px_rgba(255,216,77,0.55)]" data-testid="services-index-cta-quote">
              {c.ctaQuote} <ArrowRight size={18} />
            </button>
            <a href="https://wa.me/41782321850?text=Bonjour%20Mopper%2C%20j%27aimerais%20en%20savoir%20plus%20sur%20vos%20services" target="_blank" rel="noopener noreferrer nofollow" className="inline-flex items-center justify-center gap-2 rounded-full bg-white/10 hover:bg-white/15 border border-white/20 text-white px-7 py-4 font-semibold text-sm md:text-base">
              <MessageCircle size={18} /> {c.ctaWhatsapp}
            </a>
          </div>
        </div>
      </section>

      {/* Présentation globale + cas d'usage */}
      <section className="py-16 md:py-24 px-6 sm:px-8 md:px-10 lg:px-14">
        <div className="mx-auto grid grid-cols-1 lg:grid-cols-[1fr_1.2fr] gap-10 lg:gap-16 items-start" style={{ maxWidth: "1760px" }}>
          <div>
            <span className="text-xs uppercase tracking-[0.22em] text-[#2D5BFF] font-bold">{c.s1Kicker}</span>
            <h2 className="mt-3 font-extrabold text-3xl md:text-5xl tracking-tighter leading-[0.95]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{c.s1H2}</h2>
            <p className="mt-5 text-[#4B5563] text-base md:text-lg leading-relaxed">{c.s1P}</p>
          </div>
          <ul className="grid sm:grid-cols-2 gap-3">
            {c.useCases.map((u, i) => (
              <li key={i} className="flex items-start gap-3 p-4 bg-white rounded-2xl border border-gray-100">
                <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#EAF0FF] text-[#2D5BFF] shrink-0"><Check size={13} strokeWidth={3} /></span>
                <span className="text-[14px] text-[#0A0F2C] leading-relaxed">{u}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Liste des services */}
      <section id="liste" className="py-12 md:py-20 bg-white px-6 sm:px-8 md:px-10 lg:px-14">
        <div className="mx-auto" style={{ maxWidth: "1760px" }}>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
            <div>
              <span className="text-xs uppercase tracking-[0.22em] text-[#2D5BFF] font-bold">{c.s2Kicker}</span>
              <h2 className="mt-3 font-extrabold text-3xl md:text-5xl tracking-tighter leading-[0.95]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{c.s2H2}</h2>
            </div>
            <Link to="/devis" className="inline-flex items-center gap-2 rounded-full border border-[#0A0F2C]/15 bg-white px-5 py-2.5 text-sm font-semibold text-[#0A0F2C] hover:border-[#2D5BFF] hover:text-[#2D5BFF] transition-colors shrink-0">
              {c.s2Cta} <ArrowRight size={16} />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {SERVICES.map((s) => {
              const Icon = SERVICE_ICONS[s.slug] || Sparkles;
              const name = names[s.slug] || s.name;
              const desc = cardT.desc[s.slug] || s.intro.split(".")[0] + ".";
              const anchor = cardT.anchor[s.slug] || name;
              return (
                <article key={s.slug} className="group bg-white rounded-2xl p-6 border border-gray-100 hover:border-[#2D5BFF] hover:shadow-lg transition-all" data-testid={`services-index-card-${s.slug}`}>
                  <div className="w-11 h-11 rounded-xl bg-[#EAF0FF] text-[#2D5BFF] flex items-center justify-center mb-4"><Icon size={20} /></div>
                  <h3 className="font-bold text-lg text-[#0A0F2C]">{name}</h3>
                  <p className="mt-2 text-[13px] text-[#4B5563] leading-relaxed">{desc}</p>
                  <Link to={`/services/${s.slug}`} className="mt-4 inline-flex items-center gap-1.5 text-[#2D5BFF] font-semibold text-sm group-hover:gap-2.5 transition-all">
                    {anchor} <ArrowRight size={14} />
                  </Link>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* Services combinables / Comment ça se passe */}
      <section className="py-16 md:py-24 px-6 sm:px-8 md:px-10 lg:px-14">
        <div className="mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12" style={{ maxWidth: "1760px" }}>
          <div className="bg-white rounded-3xl p-8 md:p-10 border border-gray-100">
            <span className="text-xs uppercase tracking-[0.22em] text-[#2D5BFF] font-bold">{c.combKicker}</span>
            <h2 className="mt-3 font-extrabold text-2xl md:text-3xl tracking-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{c.combH2}</h2>
            <p className="mt-4 text-[#4B5563] leading-relaxed">{c.combP}</p>
            <ul className="mt-5 space-y-2 text-sm text-[#0A0F2C]">
              {c.combBullets.map((b, i) => (
                <li key={i} className="flex items-start gap-2"><Check size={14} className="mt-1 text-[#2D5BFF]" /> {b}</li>
              ))}
            </ul>
          </div>
          <div className="bg-[#0A0F2C] text-white rounded-3xl p-8 md:p-10">
            <span className="text-xs uppercase tracking-[0.22em] text-[#FFD84D] font-bold">{c.stepsKicker}</span>
            <h2 className="mt-3 font-extrabold text-2xl md:text-3xl tracking-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{c.stepsH2}</h2>
            <ol className="mt-5 space-y-3 text-sm">
              {c.steps.map((st, i) => (
                <li key={i}><strong className="text-[#FFD84D]">{i + 1}.</strong> {st}</li>
              ))}
            </ol>
            <Link to="/devis" className="mt-7 inline-flex items-center gap-2 rounded-full bg-[#FFD84D] text-[#0A0F2C] px-5 py-2.5 font-bold text-sm hover:bg-white transition-all">
              {c.stepsCta} <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* Zones */}
      <section className="py-12 md:py-20 bg-[#2D5BFF] text-white hero-noise px-6 sm:px-8 md:px-10 lg:px-14">
        <div className="mx-auto" style={{ maxWidth: "1760px" }}>
          <span className="text-xs uppercase tracking-[0.22em] text-[#FFD84D] font-bold inline-flex items-center gap-2"><MapPin size={12} /> {c.zonesKicker}</span>
          <h2 className="mt-3 font-extrabold text-3xl md:text-5xl tracking-tighter leading-[0.95] max-w-3xl" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{c.zonesH2}</h2>
          <p className="mt-5 text-white/85 text-base md:text-lg max-w-3xl leading-relaxed">
            {c.zonesP1}<Link to="/zones/vaud" className="underline decoration-[#FFD84D] underline-offset-4 hover:text-[#FFD84D]">{c.zonesVaud}</Link>{c.zonesP2}
          </p>
          <div className="mt-7 flex flex-wrap gap-2">
            {CANTONS_SEO.map((cn) => (
              <Link key={cn.slug} to={`/zones/${cn.slug}`} className="inline-flex items-center gap-1.5 rounded-full bg-white/10 hover:bg-white/20 border border-white/15 text-white px-4 py-2 text-sm font-semibold transition-colors" data-testid={`services-index-zone-${cn.slug}`}>
                <MapPin size={13} /> {cn.name}
              </Link>
            ))}
          </div>
          <div className="mt-8">
            <Link to="/zones" className="inline-flex items-center gap-2 rounded-full bg-[#FFD84D] hover:bg-white text-[#0A0F2C] px-6 py-3 text-sm font-extrabold transition-all" data-testid="services-index-link-zones">
              {c.zonesCta} <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA final */}
      <section className="bg-[#0A0F2C] text-white py-16 md:py-24 px-6 sm:px-8 md:px-10 lg:px-14">
        <div className="mx-auto flex flex-col md:flex-row md:items-end md:justify-between gap-8" style={{ maxWidth: "1760px" }}>
          <div className="max-w-2xl">
            <h2 className="font-extrabold text-3xl md:text-5xl lg:text-6xl tracking-tighter leading-[0.95]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              {c.finalH2a}<span className="text-[#FFD84D]">{c.finalH2b}</span>&nbsp;?
            </h2>
            <p className="mt-5 text-white/85 md:text-lg">{c.finalP}</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 shrink-0">
            <button onClick={() => navigate("/devis")} className="inline-flex items-center justify-center gap-2 rounded-full bg-[#FFD84D] text-[#0A0F2C] px-7 py-4 font-extrabold text-base hover:bg-white transition-all" data-testid="services-index-final-cta">
              {c.finalCta} <ArrowRight size={18} />
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
